import * as React from 'react';

import type { ExportKind } from '@/api/exports-client';
import { useExportsClient } from '@/api/exports-client-context';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { CampaignRecipientsExportSection } from './campaign-recipients-export-section';

// =============================================================================
// exports-screen.tsx — Écran A « Exports référentiel » (Admin).
// =============================================================================
// EXPORT = γ SYNCHRONE (GATE_0 S10) : chaque bouton déclenche un TÉLÉCHARGEMENT
// DIRECT (GET → fichier CSV). PAS de job/async/polling, PAS de Domain Event. Les 4
// exports (contrat D3 figé, AdminSessionGuard) :
//   - fournisseurs  GET /admin/suppliers/export.csv
//   - clients       GET /admin/clients/export.csv
//   - CGU           GET /admin/cgu/export.csv
//   - compétences   GET /admin/skills/export.csv
//
// Le déclenchement passe par l'ExportsClient (DI seam) : impl navigateur réelle en
// prod, double déterministe en test (capture l'URL). L'autorité (RBAC, K-anonymity,
// génération CSV) reste l'API.
//
// WCAG 2.1 AA : chaque export = un bouton avec libellé explicite + description liée
// (aria-describedby) ; navigation clavier native ; aucune dépendance couleur.

interface ExportDescriptor {
  readonly kind: ExportKind;
  readonly title: string;
  readonly description: string;
}

const EXPORTS: readonly ExportDescriptor[] = [
  {
    kind: 'suppliers',
    title: 'Fournisseurs',
    description:
      'Référentiel des fournisseurs référencés (établissements et états).',
  },
  {
    kind: 'clients',
    title: 'Clients',
    description: 'Référentiel des clients bénéficiaires.',
  },
  {
    kind: 'cgu',
    title: 'Signatures CGU',
    description: 'Registre des signatures de conditions générales.',
  },
  {
    kind: 'skills',
    title: 'Compétences',
    description: 'Référentiel des compétences et de leurs familles.',
  },
];

export function ExportsScreen(): React.JSX.Element {
  const exportsClient = useExportsClient();

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Exports référentiel
        </h1>
        <p className="text-muted-foreground">
          Téléchargez les référentiels au format CSV. Chaque export est un
          téléchargement direct.
        </p>
      </header>

      <ul className="grid gap-4 sm:grid-cols-2">
        {EXPORTS.map((item) => {
          const descId = `export-${item.kind}-description`;
          return (
            <li key={item.kind}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{item.title}</CardTitle>
                  <CardDescription id={descId}>
                    {item.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    type="button"
                    onClick={() => exportsClient.download(item.kind)}
                    aria-describedby={descId}
                    aria-label={`Télécharger l'export CSV : ${item.title}`}
                  >
                    Télécharger le CSV
                  </Button>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>

      <section aria-labelledby="campaign-recipients-export-heading" className="space-y-4">
        <h2
          id="campaign-recipients-export-heading"
          className="text-lg font-semibold tracking-tight"
        >
          Destinataires de campagne
        </h2>
        <CampaignRecipientsExportSection />
      </section>
    </div>
  );
}
